import { formatDate, formatDateOnly } from "../../Helper/generalhelper";
import { valuesAreEquivalent } from "./scoopChangedFields";

const EXTERNAL_LOG_STATUSES = new Set(["external_res", "external resource"]);

const HIDDEN_EXTERNAL_KEYS = new Set([
  "created_by",
  "dtCreationDate",
  "dtUpdatedDate",
  "iEditedBy",
  "modified_by",
  "vUserName",
  "vTimeZoneCity",
  "vPassword",
  "org_pass",
  "S3vProfilePic",
  "image",
  "address2Detail",
  "activation_token",
  "iUserId",
]);

// Labels from AddUpdateExternalResource Step1 Form.js. Unknown keys stay hidden.
const EXTERNAL_FIELD_LABELS = {
  vFirstName: "First Name",
  vLastName: "Last Name",
  companyName: "Company Name",
  vEmailAddress: "Email Address",
  vSecondaryEmailAddress: "Secondary Email Address",
  vWebsite: "Website",
  vSkypeName: "Teams/Slack Username",
  tMemo: "Memo",
  iResourceNumber: "Profile Number",
  eUserStatus: "Resource Status",
  iGender: "Gender",
  freelancer: "Freelancer Type",
  freelance_currency: "Currency Code",
  second_currency: "Second Currency",
  specialization: "Specialization",
  dtBirthDate: "Date of Birth",
  dtLast_job: "Last Job",
  iMobile: "Mobile",
  vAddress1: "Street",
  vTimeZone: "TimeZone",
  is_available: "See absent list",
};

const ADDRESS_PARTS = [
  ["address1_street_number", "Street"],
  ["address1_locality", "City"],
  ["address1_administrative_area_level_1", "State"],
  ["address1_country", "Country"],
  ["address1_postal_code", "Zipcode"],
];

const GENDER_LABELS = {
  1: "Male",
  2: "Female",
  3: "Other",
};

const FREELANCER_LABELS = {
  freelancer: "Freelance",
  translation: "Translation Agency",
};

const PREVIEW_LIMIT = 4;

export function isExternalResourceLog(logStatus) {
  return EXTERNAL_LOG_STATUSES.has(String(logStatus || "").trim().toLowerCase());
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function looksLikeJson(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function parseMaybeJson(value) {
  if (value && typeof value === "object") return value;
  if (!looksLikeJson(value)) return value;
  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return value;
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function isBlankDate(value) {
  if (isEmptyValue(value)) return true;
  const raw = String(value).trim().toLowerCase();
  return raw === "invalid date" || raw.startsWith("0000-00-00");
}

function isDiffShape(change) {
  if (!change || typeof change !== "object" || Array.isArray(change)) return false;
  return (
    Object.prototype.hasOwnProperty.call(change, "old") ||
    Object.prototype.hasOwnProperty.call(change, "new")
  );
}

function lookupName(value, nameMap) {
  if (isEmptyValue(value)) return "";
  const id = String(value).trim();
  return nameMap?.[id] || id;
}

function formatText(value) {
  if (isEmptyValue(value)) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatDateField(value) {
  if (isBlankDate(value)) return "";
  return formatDate(String(value).trim(), { includeTime: false });
}

function formatGender(value) {
  if (isEmptyValue(value)) return "";
  return GENDER_LABELS[String(value).trim()] || String(value);
}

function formatFreelancer(value) {
  if (isEmptyValue(value)) return "";
  const raw = String(value).trim();
  return FREELANCER_LABELS[raw] || raw;
}

function formatCurrencyCode(value) {
  if (isEmptyValue(value)) return "";
  return String(value).split(",")[0].trim();
}

function formatSpecialization(value, nameMap) {
  if (isEmptyValue(value)) return "";
  return String(value)
    .split(",")
    .map(id => id.trim())
    .filter(id => id && id !== "0")
    .map(id => lookupName(id, nameMap))
    .filter(Boolean)
    .join(", ");
}

function formatMobile(value) {
  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return String(parsed.mobileNumber || "").trim();
  }
  return isEmptyValue(value) ? "" : String(value);
}

function formatAbsentDate(value) {
  if (isBlankDate(value)) return "";
  return formatDateOnly(value);
}

function formatAbsentList(value) {
  const parsed = parseMaybeJson(value);
  if (isEmptyValue(parsed)) return "";
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  return rows
    .map(row => {
      if (!row || typeof row !== "object") return "";
      const from = formatAbsentDate(row.dateFrom);
      const to = formatAbsentDate(row.dateTo);
      if (from && to) return `${from} to ${to}`;
      return from || to;
    })
    .filter(Boolean)
    .join("\n");
}

function parseAddress(value) {
  const parsed = parseMaybeJson(value);
  const map = {};
  (Array.isArray(parsed) ? parsed : []).forEach(row => {
    if (!row || row.id == null) return;
    map[String(row.id)] = isEmptyValue(row.value) ? "" : String(row.value).trim();
  });
  return map;
}

function formatFieldValue(fieldKey, value, lookups) {
  if (fieldKey === "eUserStatus") return lookupName(value, lookups.statusNames);
  if (fieldKey === "iGender") return formatGender(value);
  if (fieldKey === "freelancer") return formatFreelancer(value);
  if (fieldKey === "freelance_currency" || fieldKey === "second_currency") return formatCurrencyCode(value);
  if (fieldKey === "specialization") return formatSpecialization(value, lookups.specializationNames);
  if (fieldKey === "dtBirthDate" || fieldKey === "dtLast_job") return formatDateField(value);
  if (fieldKey === "iMobile") return formatMobile(value);
  if (fieldKey === "is_available") return formatAbsentList(value);
  return formatText(value);
}

function pushChange(rows, key, label, oldValue, newValue) {
  if (oldValue === newValue) return;
  rows.push({ key, label, old: oldValue, new: newValue });
}

export function getExternalResourceChangedFields(details, lookups = {}) {
  const changedFields = parseChangedFields(details);
  if (!changedFields) return [];

  const rows = [];

  Object.keys(changedFields).forEach(key => {
    if (HIDDEN_EXTERNAL_KEYS.has(key)) return;
    const change = changedFields[key];
    if (!isDiffShape(change)) return;
    if (valuesAreEquivalent(change.old, change.new, key)) return;

    if (key === "address1Detail") {
      const oldAddress = parseAddress(change.old);
      const newAddress = parseAddress(change.new);
      ADDRESS_PARTS.forEach(([partId, label]) => {
        pushChange(
          rows,
          `address1Detail:${partId}`,
          label,
          oldAddress[partId] || "",
          newAddress[partId] || ""
        );
      });
      return;
    }

    if (!EXTERNAL_FIELD_LABELS[key]) return;
    pushChange(
      rows,
      key,
      EXTERNAL_FIELD_LABELS[key],
      formatFieldValue(key, change.old, lookups),
      formatFieldValue(key, change.new, lookups)
    );
  });

  return rows;
}

export function getExternalResourceChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
